import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import ProtectedRoute from '@/components/ProtectedRoute';
import { UserPermissions } from '@/enums/user-permissions.enum';

// Lazy load views
const Licenses = lazy(() => import('@/views/Licenses.tsx'));
const Users = lazy(() => import('@/views/Users.tsx'));
const Login = lazy(() => import('@/views/Login.tsx'));
const Logout = lazy(() => import('@/views/Logout.tsx'));
const WhatsappPermission = lazy(() => import('@/views/WhatsappPermission.tsx'));
const WhatsappPermissionGranted = lazy(() => import('@/views/WhatsappPermissionGranted.tsx'));
const ReprocessWebhooks = lazy(() => import('@/views/ReprocessWebhooks.tsx'));
const CleanLogs = lazy(() => import('@/views/CleanLogs.tsx'));
const NotFound = lazy(() => import('@/views/errors/NotFound.tsx'));
const Forbidden = lazy(() => import('@/views/errors/Forbidden.tsx'));

// Loading component
const LoadingFallback = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
    }}
  >
    <CircularProgress />
  </Box>
);

const AppRouter: React.FC = () => {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        {/* Root redirect */}
        <Route path="/" element={<Navigate to="/licenses" replace />} />

        {/* Protected routes */}
        <Route
          path="/licenses"
          element={
            <ProtectedRoute>
              <Licenses />
            </ProtectedRoute>
          }
        />
        <Route
          path="/users"
          element={
            <ProtectedRoute requiredPermission={UserPermissions.IsAdmin}>
              <Users />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reprocess-webhooks"
          element={
            <ProtectedRoute requiredPermission={UserPermissions.CanReprocess}>
              <ReprocessWebhooks />
            </ProtectedRoute>
          }
        />

        <Route
          path="/clean-logs"
          element={
            <ProtectedRoute requiredPermission={UserPermissions.CanCleanLogs}>
              <CleanLogs />
            </ProtectedRoute>
          }
        />

        {/* Public routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/logout" element={<Logout />} />
        <Route path="/whatsapp/whatsapp-permission" element={<WhatsappPermission />} />
        <Route path="/whatsapp/whatsapp-permission/granted" element={<WhatsappPermissionGranted />} />

        {/* Error routes */}
        <Route path="/errors/forbidden" element={<Forbidden />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

export default AppRouter;
